import React, { useState, useRef, useEffect } from 'react';
import server from '../../api/server';
import "../loader.css"
import { useToast } from '../../context/ToastContext.jsx';
import SampleTestModal from '../layouts/SampleTestModal.jsx'
import { IoChatbubbleOutline } from 'react-icons/io5';
import FeedbackSection from '../layouts/FeedbackSection.jsx';
import { Save, Upload, Download } from 'lucide-react';
import DeployConfirmationModal from '../layouts/DeployConfirmationModal.jsx';
import { getApiToastMessage } from '../../utils/apiToastMessage.js';
import ConfigurationDiffButton from '../layouts/ConfigurationDiffButton.jsx';

const TemplatesConfigure = ({clientID, serviceID, module_name, setCloseModal, config_data}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false);
  const [current_version, setCurrent_version] = useState(0)
  const [templates, setTemplates] = useState(null);
  const [moduleActive, setModuleActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [configType, setConfigType] = useState('Live Version')
  const [showConfigDropdown, setShowConfigDropdown] = useState(false)
  const [moduleSaveDraftConfiguration, setModuleSaveDraftConfiguration] = useState(null)
  const { showToast } = useToast();
  const [showSampleTest, setShowSampleTest] = useState(false)
  const [showSampleTestModal, setShowSampleTestModal] = useState(false)
  const audioRef = useRef(null);
  const [showMoreMap, setShowMoreMap] = useState({});
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  
  // Upload and Download Configuration Functions
  const handleDownloadConfig = () => {
    try {
      const configData = {
        templates: templates,
        moduleActive: moduleActive
      };

      const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `templates_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Configuration downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading configuration:', error);
      showToast('Error downloading configuration', 'error');
    }
  };

  const handleUploadConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const configData = JSON.parse(e.target.result);
        setLoading(true);
        const validationConfig = {
          configuration: {...(configData.templates || {})},
          active: configData.moduleActive === true ? 1 : 0
        };
        const validationResp = await server.validateModuleJsonConfiguration({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'templates', configuration: validationConfig});
        if (validationResp.status !== 200) {
          showToast(await getApiToastMessage(validationResp, 'Invalid configuration'), 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        
        // Validate the uploaded configuration
        if (!configData.templates) {
          throw new Error('Invalid configuration format');
        }

        // Update the state with uploaded configuration
        if (configData.templates) {
          setTemplates(configData.templates);
        }
        
        if (configData.moduleActive !== undefined) {
          setModuleActive(configData.moduleActive);
        }

        showToast(await getApiToastMessage(validationResp, 'Configuration uploaded successfully!'), 'success');
        setLoading(false);
      } catch (error) {
        console.error('Error parsing uploaded configuration:', error);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
        setLoading(false);
      }
    };
    
    reader.readAsText(file);
  };

  const toggleShowMore = (key) => {
    setShowMoreMap(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const handleCloseSampleTestModal = () => {
    setShowSampleTestModal(false)
  }
  const segmentRefs = useRef([]);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null);
  const scrollToSegment = (idx) => {
    const el = segmentRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };  
  const toggleSegment = (item, idx, link) => {
    setSelectedSegmentIdx(idx);
    scrollToSegment(idx);
    if(!link){
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = item.start; // Seek to the start time of the segment
      audioRef.current.play(); // Play the audio
    }
  };
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  function formatEpochTime(epochMillis) {
    if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) {
      return ""
    }
  
    const date = new Date(epochMillis);
    return date.toLocaleString(); // e.g., "5/29/2025, 2:30:45 PM"
  }
  const handleAudioTimeUpdate = (segments) => {
    const currentTime = audioRef.current.currentTime;

    // Find the segment corresponding to the current audio time
    segments.forEach((segment, idx) => {
      if (currentTime >= segment.start && currentTime <= segment.end) {
        if (segmentRefs.current[idx]) {
          // Scroll the corresponding segment into view if it's not already visible
          segmentRefs.current[idx].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });

          // Highlight the selected segment
          setSelectedSegmentIdx(idx);
        }
      }
    });
  };
  const getBorderColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"4px solid #6f6ffe"
      case 'agent':
        return"4px solid #3B82F6"
      case 'bot':
        return"4px solid gray"
      case 'system':
        return "4px solid gray"
      default:
        break;
    }
  }
  const getBackgroundColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"rgb(249, 250, 251)"
      case 'agent':
        return"rgb(249, 250, 251)"
      case 'bot':
        return"rgb(249, 250, 251)"
      case 'system':
        return"rgb(249, 250, 251)"
      default:
        break;
    }
  }

  const toggleSample = (sample) => {
    setExpandedSample(expandedSample === sample ? null : sample);
    setActiveTab('sms')
    setExpandedOutput(false)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const toggleOutput = () => {
    setExpandedOutput(!expandedOutput);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleToggle = (type) => {
    if (type === 'all') {
        const anyEnabled = templates.email.active || templates.sms.active || templates.whatsapp.active;
    
        setTemplates({
          ...templates,
          email: { ...templates.email, active: !anyEnabled },
          whatsapp: { ...templates.whatsapp, active: !anyEnabled },
          sms: { ...templates.sms, active: !anyEnabled }
        });
        return;
      }
    setTemplates({
      ...templates,
      [type]: {
        ...templates[type],
        active: !templates[type].active
      }
    });
  };

  const handlePromptChange = (type, value) => {
    setTemplates({
      ...templates,
      [type]: {
        ...templates[type],
        instruction: value
      }
    });
  };
  const [expandedSample, setExpandedSample] = useState('sample1');
  const [expandedOutput, setExpandedOutput] = useState(true);
  const [activeTab, setActiveTab] = useState('sms');
  useEffect(() => {
    if (expandedSample && audioRef.current) {
      // Reset audio player
      audioRef.current.load();
    }
  }, [expandedSample]);
  const [email, setEmail] = useState(null)
  const [defaultActive, setDefaultActive] = useState(false)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [moduleName, setModuleName] = useState(null)
  const [defaultConfig, setDefaultConfig] = useState(null)
  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const getDefaultModuleConfigs = async () => {
    try {
      setLoading(true);
      let res = await server.getDefaultModuleConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if (resp.data){
          setDefaultConfig(resp.data.configuration)
        }
      }
    setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const getModuleConfig = async() => {
    try {
      let res = await server.getClientModuleConfig(client_id, service_id,email,'apps',undefined, moduleName);
      if(res.status === 200){
        let resp = await res.json()
        setTemplates(resp.data.configuration)
        setModuleActive(resp.data.active===1 ? true : false)
        setModuleSaveDraftConfiguration(resp.data.configuration)
        setCurrent_version(resp.data.version)
        // setDefaultConfig(resp.data.default.configuration)
      }
    } catch (error) {
    }
  }
  const getModuleDraftSaveConfiguration = async () => {
    try {
      let res = await server.getModuleDraftSaveConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data){
          setModuleSaveDraftConfiguration(resp.data.configuration)
          setTemplates(resp.data.configuration)
        setModuleActive(resp.data.active === 1 ? true : false)
        }else{
          showToast('No saved draft.', "error")
          setConfigType('Live Version')
        }
        // resetToDefault('draft')
      }
    } catch (error) {
    }
  }
  const handleGenerateSample = async(sample_flag) => {
    try {
      setLoading(true)
      let config = {'configuration': {...templates}, 'active': moduleActive===true ? 1 : 0}
      let resp = await server.generateSampleModuleOutputs({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'templates', configuration: config, 'sample_set_flag': sample_flag});
      if(resp.status === 200){
        let respData = await resp.json()
        setSamples(respData.data)
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }
  const getProjectStatus = async () => {
    try {
      let res = await server.getProjectStatus(client_id, service_id, email, 'apps', undefined, window.location.href)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data && resp.data === 'completed'){
          setShowSampleTest(true);
        }
      }
    } catch (error) {
    }
  }
  const deployModule = async(versionDescription) => {
    try {
      setLoading(true)
      let deployed = false;
      let errorMessage = 'Failed to deploy module';
      let config = {'configuration': {...templates}, 'active': moduleActive===true ? 1 : 0}
      let resp = await server.deployModule({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'templates', configuration: config, default: defaultActive, 'version_changed': current_version, version_description: versionDescription});
      if(resp.status === 200){
        showToast('Module deployed successfully', "success")
        deployed = true;
        setLoading(false)
      }else{
        try {
          const errData = await resp.clone().json();
          errorMessage = errData?.message || errData?.error || errorMessage;
        } catch (_) {}
      }
      if (deployed) {
        setCloseModal();
      } else {
        showToast(errorMessage, 'error');
        setLoading(false)
      }
      setLoading(false)
    }catch(error){
      showToast('Failed to deploy module', 'error');
      setLoading(false)
    }
  }
  const resetToDefault = () => {
    setDefaultActive(true)
    setTemplates(defaultConfig)
    setModuleActive(true)
    // showToast('Configuration reset to default', "success")
  }
  const handleUpdateConfig = async () => {
    try {
      let config = {'configuration': {...templates}, 'active': moduleActive===true ? 1 : 0}
      let res = await server.postModuleDraftSaveConfiguration({client_id, service_id, email, module_name: 'templates', 'configuration': config, 'menu': 'apps', 'submenu': undefined})
      if(res.status === 200) {
        showToast(await getApiToastMessage(res, 'Draft saved successfully'), "success")
        setLoading(false)
      } else {
        showToast(await getApiToastMessage(res, 'Failed to save draft'), "error")
      }
    } catch (error) {
      showToast('Failed to save draft', "error")
    }
  }
  // Load new audio when sample changes
  useEffect(() => {
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
        if (user && clientID) {
          setEmail(user.email);
          setClient_id(clientID);
        }
        if (serviceID && module_name) {
          setService_id(serviceID);
          setModuleName(module_name)
        }
      }, [clientID, serviceID, module_name]);
    
      useEffect(() => {
        if (client_id && service_id && moduleName && email) {
          getModuleConfig();
          getDefaultModuleConfigs()
          getProjectStatus()
        }
      }, [client_id, service_id, moduleName, email]);
      useEffect(()=> {
        if(configType === 'Saved Draft'){
          getModuleDraftSaveConfiguration();
        }
        if(configType === 'Default'){
          resetToDefault();
        }
        if(configType === 'Live Version' && client_id && service_id && moduleName && email){
          getModuleConfig();
        }
      }, [configType])
      useEffect(()=> {
        if(moduleSaveDraftConfiguration){
      let config = {'configuration': {...templates}, 'active': moduleActive===true ? 1 : 0}
          config_data(config)
        }
      }, [moduleSaveDraftConfiguration, moduleActive, templates])

  const getCurrentConfigForDiff = () => ({ ...(templates || {}) });
  const getLoadedConfigForDiff = () => (
    configType === 'Default' ? defaultConfig : moduleSaveDraftConfiguration
  );

  // Configuration Screen
  if (!showPreview) {
    return (
      <div className="p-3">
        <div className="mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">Templates <span className='inline-flex items-center px-2 py-1 rounded-full whitespace-nowrap font-normal text-xs bg-gray-200 ml-1' style={{color: "gray"}}>{configType.replace(/^Reset to /, '')}</span></h1>
              <p className="text-gray-500 text-sm mt-1">Configure and customize response templates</p>
              
              {/* Configuration Import/Export Buttons */}
              {role === 'superadmin' && <div className="flex items-center gap-2 mt-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadConfig}
                    className="hidden"
                    id="config-upload"
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center text-sm"
                    onClick={() => document.getElementById('config-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Config
                  </button>
                </label>
                
                <button
                  type="button"
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center text-sm"
                  onClick={handleDownloadConfig}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Config
                </button>
              </div>}
            </div>
            <div style={{display: "flex", alignItems: "center"}}>
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input class="global-input"
                    type="checkbox" 
                    checked={moduleActive} 
                    onChange={(e) => setModuleActive(moduleActive===true ? false : true)}
                    className="sr-only global-input"
                    id="toggle-summary"
                  />
                  <label 
                    htmlFor="toggle-summary"
                    className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive===true ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span 
                      className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive===true ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </label>
                </div>
                <button 
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center"
                  onClick={handleUpdateConfig}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </button>
                <ConfigurationDiffButton
                  title="Template Changes"
                  originalConfig={getLoadedConfigForDiff}
                  modifiedConfig={getCurrentConfigForDiff}
                />
                {showSampleTest && <button 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                onClick={() => setShowSampleTestModal(true)}
              >
                Manage Test Set
              </button>}
                <div className="relative inline-block mx-2">
                  <button
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                    onClick={() => setShowConfigDropdown((prev) => !prev)} style={{width: "10rem", justifyContent: "space-between"}}
                    type="button"
                  >
                    {configType}
                    <span className="ml-2">
                      {showConfigDropdown ? (
                        // Up arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        // Down arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {showConfigDropdown && (
                    <div className="absolute z-10 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {['Live Version', 'Saved Draft', 'Default'].map(option => (
                        <div
                          key={option}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${configType === option ? 'text-indigo-600' : ''}`}
                          onClick={() => {
                            setConfigType(option);
                            setShowConfigDropdown(false);
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* <ModernTimeDropdown width="w-full mx-2" placeholder="Select Configuration" value={configType} onChange={(e)=>setConfigType(e.target.value)} options={['Reset to Default', 'Reset to Saved Draft', 'New Configuration']}/> */}
                {/* <button 
                className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 mr-2" style={{height: "40px", borderRadius: "4px"}}
                onClick={resetToDefault}
              >
                Reset to Default
              </button> */}
                <button 
                className="px-4 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2" style={{height: "40px", borderRadius: "4px"}}
                onClick={() => {setShowPreview(true); handleGenerateSample(1);}}
                >
                Preview
                </button>
            </div>
          </div>
          

          <div className="bg-white rounded-lg shadow mb-6" style={{height: "68vh", overflow: "auto"}}>
            <div className="p-3 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-800" style={{fontSize: "1.1rem"}}>Email Templates</h2>
              </div>
                <div className="mt-4">
                  <label htmlFor="email-prompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    id="email-prompt"
                    rows="6"
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={templates?.email.instruction}
                    onChange={(e) => handlePromptChange('email', e.target.value)} style={{fontSize: '0.9rem'}}
                  ></textarea>
                </div>
              
            </div>

            <div className="p-3 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-800" style={{fontSize: "1.1rem"}}>WhatsApp Templates</h2>
              </div>
                <div className="mt-4">
                  <label htmlFor="whatsapp-prompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    id="whatsapp-prompt"
                    rows="6"
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={templates?.whatsapp.instruction}
                    onChange={(e) => handlePromptChange('whatsapp', e.target.value)} style={{fontSize: '0.9rem'}}
                  ></textarea>
                </div>
              
            </div>

            <div className="p-3">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-800" style={{fontSize: "1.1rem"}}>SMS Templates</h2>
              </div>
                <div className="mt-4">
                  <label htmlFor="sms-prompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    id="sms-prompt"
                    rows="6"
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={templates?.sms.instruction}
                    onChange={(e) => handlePromptChange('sms', e.target.value)} style={{fontSize: '0.9rem'}}
                  ></textarea>
                </div>
              
            </div>
          </div>
        </div>
        {
          showSampleTestModal && (
            <div>
              <div style={{position: "fixed", left: "2%", top: "6.5%", width: "96%", height: "90%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                zIndex: "999999999", padding: "0 15px"}}>
                <SampleTestModal clientID={client_id} serviceID={service_id} emailID={email} moduleName='templates' onClose={handleCloseSampleTestModal}/>
              </div>
            </div>
          )
        }
      </div>
    );
  }
  
  // Preview Screen
  return (
    <div className="p-3 min-h-screen">
      <div className="mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Templates</h1>
            <p className="text-gray-500 text-sm mt-1" style={{fontSize: "0.9rem"}}>Sample results</p>
          </div>
          <div className="flex space-x-3">
            <button 
              className="px-4 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none" style={{height: "40px", borderRadius: "4px"}}
              onClick={() => {setShowPreview(false); setSamples([]); handleGenerateSample(0);}}
            >
              Go Back
            </button>
            <button onClick={() => {handleGenerateSample(); setSamples([]);}} className="px-4 py-1 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none" style={{height: "40px", borderRadius: "4px"}}>
              Run Different Sample
            </button>
            <ConfigurationDiffButton
              title="Template Changes"
              originalConfig={getLoadedConfigForDiff}
              modifiedConfig={getCurrentConfigForDiff}
              triggerLabel="Deploy"
              showTriggerIcon={false}
              disabled={loading}
              triggerClassName={`px-4 py-1 h-10 ${loading ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}  text-white rounded  focus:outline-none`}
              onNext={() => setShowDeployConfirmation(true)}
            />
          </div>
        </div>
        <DeployConfirmationModal
          isOpen={showDeployConfirmation}
          confirmationText={`Changes will be deployed in ${client_id || 'N/A'}, ${service_id || 'N/A'} and ${module_name || 'templates'}`}
          onClose={() => setShowDeployConfirmation(false)}
          onConfirm={(versionDescription) => {
            setShowDeployConfirmation(false);
            deployModule(versionDescription);
          }}
        />

        {/* Sample 1 */}
        <div style={{height: "75vh", overflow: "auto"}}>
        {
          !loading && samples.map((sample, idx) => {
            return (
              <div className="bg-white rounded-lg shadow mb-2 overflow-hidden" style={{maxHeight: "70vh", overflow: "auto"}}>
          <div 
            className="p-4 border-b border-gray-200 bg-gray-50 cursor-pointer"
            onClick={() => toggleSample(`Sample ${idx+1}`)}
          >
            <div className="flex items-center" style={{width: "100%", justifyContent: 'space-between'}}>
              <h2 className="text-lg font-medium text-gray-800">{`Sample ${idx+1}`}</h2>
              <svg 
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSample === `Sample ${idx+1}` ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
            </div>
            <div className="text-sm text-gray-600 mt-1 capitalize" style={{fontSize: "12px"}}>
                        ID: {sample.call_id} | Time: {sample.call_time} | Employee: {sample.employee_name} | Record Type: {sample.record_type}
                      </div>
          </div>
          
          {expandedSample === `Sample ${idx+1}` && (
            <div className="p-4 flex">
              {/* Output Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.65"}}>
                <div 
                  className="p-2 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center" style={{justifyContent: "space-between"}}
                >
                  
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                    </svg>
                    <h3 className="font-medium text-gray-800">Output</h3>
                  </div>

                </div>

                  <FeedbackSection key="parameters" showMoreMap={showMoreMap} toggleShowMore={toggleShowMore}
                  dataKey="Templates" data={sample.processed_analytics.parameters} maxHeight="45vh" />
              </div>
              {/* Audio Section */}
              <div className="ml-2 border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.35"}}>
                            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center">
                              <div className="flex items-center">
                              {sample.record_type === 'call' ? (
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                </svg>
                  ) : (<IoChatbubbleOutline style={{marginRight: "0.5rem", height: "30px"}}/>)}
                                <h3 className="font-medium text-gray-800">{sample.record_type === 'call' ? 'Audio' : 'Chat' }</h3>
                              </div>
                            </div>
                            {sample.record_link && <div className="p-2 bg-white">
                              <audio controls key={sample.record_link} ref={audioRef} onTimeUpdate={()=>handleAudioTimeUpdate(sample.segments)} style={{ width: "96%", margin: "0 1rem", height: "40px" }}>
                                <source src={sample.record_link} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>}
                            <hr/>
                            <div style={{fontSize: "13px", margin: "10px", overflow: 'auto', minHeight: "30vh", maxHeight: "40vh"}}>
                            {
                              sample.record_type === 'call' && Array.isArray(sample.segments) && sample.segments.map((item, idx)=> {
                                        const isSelected = selectedSegmentIdx === idx;
                                        return (
                                          <div key={`segment_${idx}`} ref={(el) => (segmentRefs.current[idx] = el)} onClick={()=>toggleSegment(item, idx, sample.record_link)} style={{marginBottom: "10px", borderLeft: isSelected ? "4px solid #6f6ffe":"", background: isSelected ? "rgba(249, 250, 251, 1)":"",
                                            padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                              <div style={{fontSize: "14px", color: "grey"}}>{item.speaker}</div>
                                              <div style={{fontSize: "14px", color: "grey"}}>{formatTime(item.start)}</div>
                                            </div>
                                            <div style={{fontSize: "15px"}}>{item.text}</div>
                                          </div>
                                        )
                                      })
                                    }
                            {
                              sample.record_type === 'chat' && Array.isArray(sample.segments) && sample.segments.map((item, idx)=> {
                                        return (
                                          <div key={`segment_${idx}`}
                                            style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                                              padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                                <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                                                  <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                                                  {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                                                </div>
                                                <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                                              </div>
                                              <div style={{fontSize: "14px"}}><span style={{whiteSpace: "pre-line"}}>{item.content?.text?.content} <br/></span></div>
                                              {item.content.image && item.content.image.url && 
                                                <div style={{fontSize: "15px", wordBreak: "break-all"}}>
                                                  <br/>
                                                Image Shared - {item.content.image.url}
                                                </div>
                                                }
                                              {
                                                item.content.buttons && item.content.buttons.length > 0 && (
                                                  <div>
                                                    <br/>
                                                    {item.actor_type === 'user' ? (
                                                      <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                                    ) : (
                                                      <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                                    )}
                                                  </div>
                                                )
                                              }
                                            </div>
                                        )
                                      })
                                    }
                            </div>
                          </div>
              
              
            </div>
          )}
        </div>
            )
          })
        }
        {loading && (
          <div className="flex justify-center items-center" style={{ height: "100%",flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesConfigure;
